<template>
  <div :class="['komodo-input']">
    <komodo-input-title
      v-if="title"
      :title="title"
      :help="help"
    />
    <slot
      v-else
      name="title"
    />
    <div
      v-click-outside-deprecated="loseFocus"
      :class="['input-box', size, `komodo-${theme}`, { disabled, errored, warning }]"
      @click="gainFocus"
    >
      <div
        v-if="$scopedSlots.left"
        class="komodo-input-left"
      >
        <slot name="left" />
      </div>
      <input
        ref="input"
        :accept="accept"
        :class="[size, `komodo-${theme}`, { disabled, errored }]"
        :value="value"
        :disabled="disabled"
        :placeholder="placeholder"
        :type="type"
        :name="name"
        :autocorrect="autocorrect"
        :autocapitalize="autocapitalize"
        :autocomplete="autocomplete"
        :spellcheck="spellcheck"
        :data-test="dataTest"
        :required="required"
        :min="min"
        :readonly="readonly"
        @input="$emit('input', $event.target.value)"
        @keyup.enter="$emit('submit', $event.target.value)"
        @change="$emit('change', $event.target.value)"
        @keydown="e => $emit('keydown', e)"
        @keydown.enter.prevent="$emit('keydown-enter-prevent')"
        @keydown.delete="e => $emit('keydown-delete', e)"
        @keydown.down="$emit('keydown-down')"
        @keydown.up="$emit('keydown-up')"
        @keydown.esc="$emit('keydown-esc')"
      >
      <div
        v-if="$scopedSlots.right"
        class="komodo-input-right"
      >
        <slot name="right" />
      </div>
    </div>
    <komodo-errors
      v-if="errored && errors.length > 0"
      :errors="errors"
    />
  </div>
</template>

<script lang="ts">
import { PropType, ref, defineComponent } from 'vue'
import KomodoInputTitle from 'shared/components/KomodoInputTitle.vue'
import KomodoErrors from 'shared/components/KomodoErrors.vue'
import { Size } from 'shared/lib/component_size'
export { Size } from 'shared/lib/component_size'
export enum Theme {
  Gray = 'gray',
  White = 'white'
}

export default defineComponent({
  name: 'TheInput',
  components: {
    KomodoErrors,
    KomodoInputTitle
  },
  props: {
    title: {
      type: String,
      default: undefined
    },
    help: {
      type: String,
      default: undefined
    },
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: undefined
    },
    name: {
      type: String,
      default: undefined
    },
    min: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    errored: {
      type: Boolean,
      default: false
    },
    warning: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    size: {
      type: String as PropType<Size>,
      default: Size.Large
    },
    autocorrect: {
      type: String,
      default: undefined
    },
    autocapitalize: {
      type: String,
      default: undefined
    },
    autocomplete: {
      type: String,
      default: undefined
    },
    spellcheck: {
      type: String,
      default: undefined
    },
    dataTest: {
      type: String,
      default: undefined
    },
    theme: {
      type: String as PropType<Theme>,
      default: Theme.White
    },
    accept: {
      type: String,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    blur: () => true,
    focus: () => true,
    change: (value: KeyboardEvent) => value,
    input: (value: KeyboardEvent) => value,
    submit: (value: KeyboardEvent) => value,
    keydown: (value: KeyboardEvent) => value,
    'keydown-enter-prevent': () => true,
    'keydown-delete': (value: KeyboardEvent) => value,
    'keydown-down': () => true,
    'keydown-up': () => true,
    'keydown-esc': () => true,
    click: (value) => value
  },
  setup (_, { emit }) {
    const focused = ref(false)
    const input = ref<InstanceType<typeof HTMLElement> | null>(null)

    const gainFocus = (event) => {
      if (!focused.value) {
        const inputValue = input.value as HTMLElement
        if (inputValue) {
          inputValue.focus()
          focused.value = true
          emit('focus')
        }
      }
      emit('click', event)
    }

    const loseFocus = () => {
      if (focused.value) {
        focused.value = false
        emit('blur')
      }
    }

    return {
      gainFocus,
      input,
      loseFocus
    }
  }
})
</script>

<style lang="scss" scoped>
$left-right-padding: 8px;
$border-width: 1px;

.komodo-input {
  display: flex;
  flex-direction: column;
  width: 100%;

  .input-box {
    @include k2-background-neutrals-card-bg-on-surface;

    border: $border-width solid $gray-4-24;
    border-radius: 4px;
    display: flex;
    overflow: hidden;
    min-height: 30 + $border-width * 2;
    align-items: center;
    justify-content: flex-start;
    font-size: 14px;
    padding-left: 8px;

    .komodo-input-left {
      padding: 0 $left-right-padding;
      display: flex;
      align-items: center;

      i {
        font-size: 16px !important;
      }
    }

    .komodo-input-right {
      padding-right: $left-right-padding;
      display: flex;
      align-items: center;
    }

    input {
      @include k2-color-neutrals-primary-text;

      padding: 4px 0;
      border: 0;
      outline: none;
      overflow: hidden;
      width: 100%;

      &::placeholder {
        @include k2-color-neutrals-primary-text;
      }
    }

    &.komodo-gray {
      border-color: $primary-blue-08;

      input {
        background-color: $primary-blue-7-opaque;
      }
    }

    &.focused {
      border-color: $action-blue-base;
    }

    &.disabled {
      border-color: $k2-neutrals-separator;
      background-color: $gray-5-08;

      &::placeholder {
        @include k2-color-neutrals-primary-text;
      }
    }

    &.errored {
      border-color: $accent-red-1-base;
    }

    &.warning {
      border-color: $accent-orange-1-base;
    }

    &.extra-small {
      height: 32px;

      .komodo-input-left {
        padding-left: 9px;
      }

      .komodo-input-right {
        padding-right: 9px;
      }

      input {
        @include font-paragraph-p5-extra-small;

        padding: 8px 9px;

        &::placeholder {
          line-height: 21px;
        }
      }
    }

    &.small {
      height: 36px;

      .komodo-input-left {
        padding-left: 9px;
      }

      .komodo-input-right {
        padding-right: 9px;
      }

      input {
        @include font-paragraph-p3-small-compact;

        padding: 10px 9px;
      }
    }
  }
}
</style>
